import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  message,
  Steps,
  Space,
  Alert,
  Modal
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  SafetyOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  LockOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text, Paragraph } = Typography;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [checkingAccountType, setCheckingAccountType] = useState(false);
  const [accountTypeWarning, setAccountTypeWarning] = useState(null);
  const captchaLoadingRef = useRef(false);
  const [captcha, setCaptcha] = useState({
    captchaId: '',
    captchaSvg: '',
    expiresIn: 0
  });

  // 获取图形验证码
  const getCaptcha = useCallback(async () => {
    if (captchaLoadingRef.current) return; // 防止重复调用
    
    captchaLoadingRef.current = true;
    try {
      const response = await API.get('/api/register/captcha');
      setCaptcha({
        captchaId: response.data.captchaId,
        captchaSvg: response.data.captchaSvg,
        expiresIn: response.data.expiresIn
      });
    } catch (error) {
      message.error('获取验证码失败：' + (error.response?.data?.message || '网络错误'));
    } finally {
      captchaLoadingRef.current = false;
    }
  }, []);

  // 组件加载时获取验证码
  useEffect(() => {
    getCaptcha();
  }, [getCaptcha]);

  // 检查账户类型
  const checkAccountType = async (username) => {
    if (!username || username.trim() === '') {
      setAccountTypeWarning(null);
      return null;
    }

    setCheckingAccountType(true);
    try {
      const response = await API.post('/api/forgot/check-account-type', {
        username: username.trim()
      });
      
      const accountType = response.data.accountType;
      
      if (accountType === 'ram') {
        setAccountTypeWarning({
          type: 'warning',
          message: 'RAM账号密码重置',
          description: 'RAM账号无法通过此页面重置密码，请联系您的主账号管理员进行密码重置操作。'
        });
        return 'ram';
      } else {
        setAccountTypeWarning(null);
        return 'main';
      }
    } catch (error) {
      // 如果用户不存在或其他错误，清除警告信息
      setAccountTypeWarning(null);
      return null;
    } finally {
      setCheckingAccountType(false);
    }
  };

  // 用户名输入框失焦时检查账户类型
  const handleUsernameBlur = (e) => {
    const username = e.target.value;
    checkAccountType(username);
  };

  const handleForgotPassword = async (values) => {
    setLoading(true);
    
    try {
      // 提交前再次检查账户类型
      const accountType = await checkAccountType(values.username);
      
      if (accountType === 'ram') {
        message.warning('RAM账号无法通过此页面重置密码，请联系主账号管理员');
        setLoading(false);
        return;
      }
      
      const response = await API.post('/api/forgot/forgot-password', {
        username: values.username,
        email: values.email,
        captchaId: captcha.captchaId,
        captchaCode: values.captchaCode
      });
      
      message.success(response.data.message);
      form.resetFields();
      setAccountTypeWarning(null); // 清除警告信息
      getCaptcha(); // 重新获取验证码
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = errorData?.message || '请求失败';
      
      // 重新获取图形验证码
      getCaptcha();
      
      // 如果错误信息包含验证码相关内容，在字段上显示错误
      if (errorMessage.includes('验证码') || errorMessage.includes('captcha')) {
        // 设置字段错误
        form.setFields([
          {
            name: 'captchaCode',
            errors: [errorMessage],
            value: '' // 同时清空字段值
          }
        ]);
      } else {
        message.error(errorMessage);
        form.setFieldsValue({ captchaCode: '' });
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: '填写信息',
      description: '输入用户名、邮箱和验证码',
      icon: <UserOutlined />
    },
    {
      title: '接收邮件',
      description: '查收重置密码邮件',
      icon: <MailOutlined />
    },
    {
      title: '重置密码',
      description: '点击邮件链接设置新密码',
      icon: <LockOutlined />
    },
    {
      title: '完成',
      description: '使用新密码登录系统',
      icon: <CheckCircleOutlined />
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f0f2f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '1000px',
      }}>
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'stretch'
        }}>
          {/* 左侧说明 */}
          <Card 
            style={{ 
              flex: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ padding: '20px' }}>
              <Title level={2} style={{ textAlign: 'center', marginBottom: '24px' }}>
                密码找回流程
              </Title>
              
              <Alert
                message="安全提示"
                description="为了保护您的账户安全，密码重置链接将在1小时后失效。请及时完成密码重置操作。"
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: '24px' }}
              />
              
              <Steps
                direction="vertical"
                current={0}
                items={steps}
                style={{ marginBottom: '24px' }}
              />
              
              {/* 帮助按钮 */}
              <div style={{ textAlign: 'center' }}>
                <Button 
                  type="default"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setHelpModalVisible(true)}
                  size="large"
                >
                  需要帮助？
                </Button>
              </div>
            </div>
          </Card>

          {/* 右侧表单 */}
          <Card 
            style={{ 
              flex: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ padding: '20px' }}>
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <Title level={2} style={{ marginBottom: '8px' }}>
                  找回密码
                </Title>
                <Text type="secondary" style={{ fontSize: '16px' }}>
                  请填写以下信息来重置您的密码
                </Text>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleForgotPassword}
                size="large"
              >
                <Form.Item
                  name="username"
                  label="用户名"
                  rules={[{ required: true, message: '请输入用户名' }]}
                >
                  <Input 
                    prefix={<UserOutlined />} 
                    placeholder="请输入您的用户名"
                    onBlur={handleUsernameBlur}
                  />
                </Form.Item>

                {/* 账户类型警告信息 */}
                {accountTypeWarning && (
                  <Alert
                    message={accountTypeWarning.message}
                    description={accountTypeWarning.description}
                    type={accountTypeWarning.type}
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                )}

                {checkingAccountType && (
                  <div style={{ 
                    textAlign: 'center', 
                    marginBottom: '16px',
                    color: '#1890ff'
                  }}>
                    <Text type="secondary">正在检查账户类型...</Text>
              </div>
            )}

                <Form.Item
                  name="email"
                  label="邮箱地址"
                  rules={[
                    { required: true, message: '请输入邮箱地址' },
                    { type: 'email', message: '请输入有效的邮箱地址' }
                  ]}
                >
                  <Input 
                    prefix={<MailOutlined />} 
                    placeholder="请输入您的邮箱地址"
                  />
                </Form.Item>

                {/* 验证码图片 */}
                <Form.Item
                  name="captchaCode"
                  label="图形验证码"
                  rules={[{ required: true, message: '请输入图形验证码' }]}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                  }}>
                    <div 
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        minWidth: '100px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff'
                      }}
                      dangerouslySetInnerHTML={{ __html: captcha.captchaSvg }}
                      onClick={getCaptcha}
                      title="点击刷新验证码"
                    />
                    <Input 
                      prefix={<SafetyOutlined />}
                      placeholder="请输入验证码"
                      maxLength={4}
                      style={{
                        textTransform: 'uppercase',
                        flex: 1,
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        height: '40px'
                      }}
                    />
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={getCaptcha}
                      title="刷新验证码"
                    >
                      刷新
                    </Button>
                  </div>
                </Form.Item>
                <div style={{ marginTop: '-20px', marginBottom: '24px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    看不清？点击验证码图片或刷新按钮更换
                  </Text>
                </div>

                <Form.Item style={{ marginTop: '32px' }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    disabled={accountTypeWarning?.type === 'warning' || checkingAccountType}
                    block
                    size="large"
                    style={{ marginBottom: '16px' }}
                  >
                    {loading ? '发送中...' : 
                     accountTypeWarning?.type === 'warning' ? 'RAM账号无法重置' :
                     '发送重置密码邮件'}
                  </Button>
                  
                  <Button 
                    type="link" 
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/login')}
                    block
                    style={{ padding: 0, height: 'auto' }}
                  >
                    返回登录页面
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Card>
        </div>
        
        {/* 帮助弹窗 */}
        <Modal
          title="密码找回帮助"
          open={helpModalVisible}
          onCancel={() => setHelpModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setHelpModalVisible(false)}>
              关闭
            </Button>
          ]}
          width={600}
        >
          <Paragraph style={{ marginBottom: '16px' }}>
            如果您在密码重置过程中遇到问题，可以尝试以下解决方案：
          </Paragraph>
          
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>• 检查邮件垃圾箱，确认重置邮件没有被误拦截</Text>
            <Text>• 确保用户名和邮箱地址输入正确</Text>
            <Text>• 重置链接1小时后会失效，请及时操作</Text>
            <Text>• 如问题持续存在，请联系系统管理员</Text>
          </Space>
        </Modal>
      </div>
    </div>
  );
};

export default ForgotPassword;
